var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Timeouts } from './framework/Timeouts';
class Block {
    constructor(instanceTag, blockType, numChannels) {
        this.instanceTag = instanceTag;
        this.blockType = blockType;
        this.numChannels = numChannels;
        const isMute = Block.muteTypes.includes(this.blockType);
        const isMuteStatus = Block.muteStatusTypes.includes(this.blockType);
        const isTelephony = Block.telephonyTypes.includes(this.blockType);
        const isVolume = Block.volumeTypes.includes(this.blockType);
        this.detected = {
            instanceTag: this.instanceTag,
            numChannels: this.numChannels,
            type: this.blockType,
            filters: {
                amplifier: isVolume,
                assistiveListeningMute: isMute,
                assistiveListeningMuteStatus: isMuteStatus,
                assistiveListeningVolume: isVolume,
                audioInputMute: isMute,
                audioInputMuteStatus: isMuteStatus,
                audioInputVolume: isVolume,
                mics: isMute,
                micsMuteStatus: isMuteStatus,
                overflowRoomMute: isMute,
                overflowRoomMuteStatus: isMuteStatus,
                overflowRoomVolume: isVolume,
                preset: false,
                receiveGain: isVolume,
                soundReinforcementMute: isMute,
                soundReinforcementMuteStatus: isMuteStatus,
                soundReinforcementVolume: isVolume,
                telephony: isTelephony,
            },
        };
    }
    canSubscribe() {
        return this.blockType !== 'RoomCombinerInterface';
    }
    get isPstn() {
        return this.blockType === 'TIControlStatusInterface';
    }
    get isVoip() {
        return this.blockType === 'VoIPControlStatusInterface';
    }
    get muteAttribute() {
        if (this.blockType === 'RoomCombinerInterface')
            return 'muteOut';
        if (this.blockType === 'LogicMeterInterface')
            return 'state';
        return 'mute';
    }
    get volumeAttribute() {
        if (this.blockType === 'RoomCombinerInterface')
            return 'levelOut';
        return 'level';
    }
    validate(config, filter) {
        if (config.instanceTag !== this.instanceTag)
            return 'incompatible';
        if (!this.detected.filters[filter])
            return 'incompatible';
        if (config.index === undefined) {
            return this.numChannels === 0 ? 'ok' : 'range';
        }
        else {
            if (config.index >= 0 && config.index <= this.numChannels) {
                return 'ok';
            }
            else {
                return 'range';
            }
        }
    }
}
Block.muteStatusTypes = [
    'BFMicInterface',
    'LevelControlInterface',
    'LogicMeterInterface',
    'MuteControlInterface',
    // RoomCombinerInterface not allowed since it doesn't support subscribing
];
Block.muteTypes = [
    'BFMicInterface',
    'LevelControlInterface',
    'MuteControlInterface',
    'RoomCombinerInterface',
];
Block.telephonyTypes = [
    'TIControlStatusInterface',
    'VoIPControlStatusInterface',
];
Block.volumeTypes = [
    'LevelControlInterface',
    'RoomCombinerInterface',
];
export class Tesira {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.blocks = [];
        this.configChannels = [];
        this.isCalling = {};
        this.timeouts = new Timeouts();
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                this.blocks.length = 0;
                let match = yield this.socket.send(`SESSION get aliases`, /^\+OK "list":\["(.+)"\]$/);
                const aliases = match[1].split('" "');
                for (const alias of aliases) {
                    match = yield this.socket.send(`"${alias}" get aveo`, /^-ERR 'aveo' is not supported by (.+)::(.+)$|^-ERR address not found:|CANNOT_DELIVER/);
                    const blockType = match[1] === undefined ? 'unknown' : match[1];
                    const attr = blockType === 'LogicMeterInterface' ? 'states' : 'numChannels';
                    match = yield this.socket.send(`"${alias}" get ${attr}`, /\+OK "value":(.+)|(-ERR .+)|(CANNOT_DELIVER)/);
                    if ((match[2] !== undefined && match[2] === '-ERR PERMISSION_DENIED') ||
                        match[3] !== undefined) {
                        continue;
                    }
                    let numChannels = 0;
                    if (match[1] !== undefined) {
                        if (blockType === 'LogicMeterInterface') {
                            numChannels = match[1].split(/\s+/).length;
                        }
                        else {
                            numChannels = parseInt(match[1], 10);
                            if (Number.isNaN(numChannels))
                                numChannels = 0;
                        }
                    }
                    else if (blockType === 'RoomCombinerInterface') {
                        numChannels = 20; // not sure how to get number of rooms
                    }
                    this.blocks.push(new Block(alias, blockType, numChannels));
                }
                return this.blocks.map(block => block.detected);
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const match = yield this.socket.send(`DEVICE get version`, /^\+OK "value":"(.+)"|-ERR/, tags);
                return (_a = match[1]) !== null && _a !== void 0 ? _a : 'unknown';
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const [instanceTag, index] = key.split('.');
                const block = this.getBlock(instanceTag);
                if (block === undefined) {
                    this.log.error(`no block with instanceTag "${instanceTag}"`);
                    return false;
                }
                const cmd = index === undefined
                    ? `"${instanceTag}" get ${block.muteAttribute}`
                    : `"${instanceTag}" get ${block.muteAttribute} ${index}`;
                const match = yield this.socket.send(cmd, /^\+OK "value":(.+)|-ERR/, tags);
                return match[1] === 'true';
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b, _c, _d, _e;
                const chan = this.getConfigChannel(key);
                if (chan === undefined) {
                    this.log.error(`no config channel for key "${key}"`);
                    return (_b = this.state.mutes.values[key]) !== null && _b !== void 0 ? _b : false;
                }
                if (chan.type === 'mute') {
                    const cmd = chan.index === undefined
                        ? `"${chan.instanceTag}" set ${chan.block.muteAttribute} ${value}`
                        : `"${chan.instanceTag}" set ${chan.block.muteAttribute} ${chan.index} ${value}`;
                    const match = yield this.socket.send(cmd, /^(\+OK)|-ERR/, tags);
                    return match[1] === '+OK' ? value : (_c = this.state.mutes.values[key]) !== null && _c !== void 0 ? _c : false;
                }
                else if (chan.type === 'presetMute') {
                    const presetName = value ? chan.mute : chan.unmute;
                    const match = yield this.socket.send(`DEVICE recallPresetByName "${presetName}"`, /^(\+OK)|-ERR/, tags);
                    return match[1] === '+OK' ? value : (_d = this.state.mutes.values[key]) !== null && _d !== void 0 ? _d : false;
                }
                this.log.error(`unexpected config channel type "${chan.type}" for key "${key}"`);
                return (_e = this.state.mutes.values[key]) !== null && _e !== void 0 ? _e : false;
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.serialNumber = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _f;
                const match = yield this.socket.send(`DEVICE get serialNumber`, /^\+OK "value":"(.+)"|-ERR/, tags);
                return (_f = match[1]) !== null && _f !== void 0 ? _f : 'unknown';
            }),
        };
        this.telephony = {
            answer: (key) => __awaiter(this, void 0, void 0, function* () {
                const chan = this.getConfigChannel(key);
                if (chan === undefined) {
                    this.log.error(`no config channel for key "${key}"`);
                    return false;
                }
                const cmd = chan.block.isPstn
                    ? `"${chan.instanceTag}" answer`
                    : `"${chan.instanceTag}" answer ${chan.index} 1`;
                yield this.socket.send(cmd, /^(\+OK)|-ERR/);
                return false;
            }),
            call: (value) => __awaiter(this, void 0, void 0, function* () {
                var _g, _h;
                for (const chan of this.configChannels) {
                    if ((value.callType === 'voip' && chan.block.isVoip) ||
                        (value.callType === 'pstn' && chan.block.isPstn)) {
                        if (chan.type !== 'telephonyControlStatus') {
                            this.log.error(`unexpected config channel type: ${chan.type}`);
                            continue;
                        }
                        const callState = (_h = (_g = this.state.calls.values[chan.key]) === null || _g === void 0 ? void 0 : _g.callState) !== null && _h !== void 0 ? _h : 'disconnected';
                        if (callState !== 'disconnected')
                            continue;
                        const cmd = chan.block.isPstn
                            ? `"${chan.instanceTag}" dial ${value.dialString}`
                            : `"${chan.instanceTag}" dial ${chan.index} 1 ${value.dialString}`;
                        // Update the call state before sending the command. It is possible that we get
                        // a status update before the +OK response. If that happens, we want the update
                        // applied to the already created call.
                        this.state.calls.call(chan.key, value.callType, value.callId, value);
                        this.isCalling[chan.key] = true;
                        let match;
                        try {
                            match = yield this.socket.send(cmd, /^(\+OK)|-ERR/);
                        }
                        finally {
                            // If the call command fails, disconnect the call.
                            if ((match === null || match === void 0 ? void 0 : match[1]) !== '+OK')
                                this.state.calls.disconnect(chan.key);
                            delete this.isCalling[chan.key];
                        }
                        return undefined;
                    }
                }
                return undefined;
            }),
            dial: (key, value) => __awaiter(this, void 0, void 0, function* () {
                const chan = this.getConfigChannel(key);
                if (chan === undefined) {
                    this.log.error(`no config channel for key "${key}"`);
                    return value;
                }
                const cmd = chan.block.isPstn
                    ? `"${chan.instanceTag}" dtmf`
                    : `"${chan.instanceTag}" dtmf ${chan.index}`;
                for (const digit of value) {
                    yield this.socket.send(`${cmd} ${digit}`, /^(\+OK)|-ERR/);
                }
                return value;
            }),
            hangup: (key) => __awaiter(this, void 0, void 0, function* () {
                const chan = this.getConfigChannel(key);
                if (chan === undefined) {
                    this.log.error(`no config channel for key "${key}"`);
                    return false;
                }
                const cmd = chan.block.isPstn
                    ? `"${chan.instanceTag}" end `
                    : `"${chan.instanceTag}" end ${chan.index} 1 `;
                yield this.socket.send(cmd, /^(\+OK)|-ERR/);
                return false;
            }),
            receiveGainRange: { max: 10, min: -10, round: 0.1 },
            reject: (key) => __awaiter(this, void 0, void 0, function* () {
                const chan = this.getConfigChannel(key);
                if (chan === undefined) {
                    this.log.error(`no config channel for key "${key}"`);
                    return false;
                }
                if (chan.block.isPstn) {
                    const answerCmd = chan.block.isPstn
                        ? `"${chan.instanceTag}" answer `
                        : `"${chan.instanceTag}" answer ${chan.index} 1 `;
                    yield this.socket.send(answerCmd, /^(\+OK)|-ERR/);
                    yield this.timeouts.delay(1000);
                }
                const endCmd = chan.block.isPstn
                    ? `"${chan.instanceTag}" end `
                    : `"${chan.instanceTag}" end ${chan.index} 1 `;
                yield this.socket.send(endCmd, /^(\+OK)|-ERR/);
                return false;
            }),
            setReceiveGain: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const chan = this.configChannels.find(chan => chan.type === 'telephonyReceiveGain' && chan.controlStatusKey === key);
                if (chan === undefined) {
                    this.log.error(`couldn't find receive gain channel for ${key}`);
                    return value;
                }
                return this.volume.set(chan.key, value, tags);
            }),
            getCalls: () => __awaiter(this, void 0, void 0, function* () {
                var _j;
                const calls = {};
                const telephonyInstanceTags = this.getTelephonyInstanceTags();
                const callStatuses = {};
                for (const instanceTag of telephonyInstanceTags) {
                    const cmd = `"${instanceTag}" get callState`;
                    const match = yield this.socket.send(cmd, /^\+OK "value":(.+)|-ERR/);
                    Object.assign(callStatuses, this.parseTesiraCallStateInfo(instanceTag, (_j = match[1]) !== null && _j !== void 0 ? _j : ''));
                }
                for (const chan of this.configChannels) {
                    if (chan.type !== 'telephonyControlStatus')
                        continue;
                    if (chan.index === undefined) {
                        this.log.error(`no index in telephony config: ${chan.key}`);
                        continue;
                    }
                    const callStatus = callStatuses[chan.key];
                    const callType = chan.block.isVoip ? 'voip' : 'pstn';
                    if (callStatus === undefined) {
                        this.log.error(`no callState response for ${chan.key}`);
                        calls[chan.key] = this.state.calls.addMissing({ callType, canDial: true });
                    }
                    else {
                        calls[chan.key] = this.state.calls.addMissing(Object.assign(Object.assign({}, callStatus), { callType, canDial: true }));
                    }
                }
                return calls;
            }),
            getMaxCalls: () => __awaiter(this, void 0, void 0, function* () {
                const maxCalls = {};
                for (const chan of this.configChannels) {
                    if (chan.block.isPstn) {
                        if (maxCalls.pstn === undefined)
                            maxCalls.pstn = 0;
                        maxCalls.pstn++;
                    }
                    else if (chan.block.isVoip) {
                        if (maxCalls.voip === undefined)
                            maxCalls.voip = 0;
                        maxCalls.voip++;
                    }
                }
                return maxCalls;
            }),
            getKey: this.getKey.bind(this),
            validate: this.validateTelephony.bind(this), // happens before constructor binding
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _k;
                const [instanceTag, index] = key.split('.');
                const block = this.getBlock(instanceTag);
                if (block === undefined) {
                    this.log.error(`no block with instanceTag "${instanceTag}"`);
                    return 0;
                }
                const cmd = index === undefined
                    ? `"${instanceTag}" get ${block.volumeAttribute}`
                    : `"${instanceTag}" get ${block.volumeAttribute} ${index}`;
                const match = yield this.socket.send(cmd, /^\+OK "value":(.+)|-ERR/, tags);
                const value = parseFloat((_k = match[1]) !== null && _k !== void 0 ? _k : '0');
                return value;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _l;
                const [instanceTag, index] = key.split('.');
                const block = this.getBlock(instanceTag);
                if (block === undefined) {
                    this.log.error(`no block with instanceTag "${instanceTag}"`);
                    return 0;
                }
                const cmd = index === undefined
                    ? `"${instanceTag}" set ${block.volumeAttribute} ${value}`
                    : `"${instanceTag}" set ${block.volumeAttribute} ${index} ${value}`;
                const match = yield this.socket.send(cmd, /^(\+OK)|-ERR/, tags);
                return match[1] === '+OK' ? value : (_l = this.state.volumes.values[key]) !== null && _l !== void 0 ? _l : 0;
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                const key = this.getKey(config);
                const [instanceTag, index] = key.split('.');
                const block = this.getBlock(instanceTag);
                if (block === undefined) {
                    this.log.error(`no block with instanceTag "${instanceTag}"`);
                    if (info.volumeType === 'trim')
                        return { max: 12, min: -12, round: 0.1 };
                    return { max: 12, min: -38, round: 0.1 }; // volume
                }
                let maxCmd = `${instanceTag} get maxLevel`;
                let minCmd = `${instanceTag} get minLevel`;
                if (index !== undefined) {
                    maxCmd += ` ${index}`;
                    minCmd += ` ${index}`;
                }
                const maxMatch = yield this.socket.send(maxCmd, /^\+OK "value":(.+)|-ERR/);
                if (maxMatch[1] === undefined) {
                    if (info.volumeType === 'trim')
                        return { max: 12, min: -12, round: 0.1 };
                    return { max: 12, min: -38, round: 0.1 }; // volume
                }
                const maxLevel = parseFloat(maxMatch[1]);
                const minMatch = yield this.socket.send(minCmd, /^\+OK "value":(.+)|-ERR/);
                const minLevel = minMatch[1] === undefined ? -100 : parseFloat(minMatch[1]);
                const gainRange = info.volumeType === 'trim' ? 24 : 50;
                let maxGain = info.volumeType === 'trim' ? 12 : 12;
                if (maxGain > maxLevel)
                    maxGain = maxLevel;
                let minGain = maxGain - gainRange;
                if (minGain < minLevel)
                    minGain = minLevel;
                this.log.debug(`jsj: ${key}: maxLevel: ${maxLevel}, minLevel: ${minLevel}, maxGain: ${maxGain}, minGain: ${minGain}`);
                return { max: maxGain, min: minGain, round: 0.1 };
            }),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                function: () => __awaiter(this, void 0, void 0, function* () {
                    let numTelephonyPolled = 0;
                    for (const chan of this.configChannels) {
                        if (chan.type === 'telephonyControlStatus') {
                            const tags = ['get.callState', 'poll'];
                            const cmd = `"${chan.instanceTag}" get callState`;
                            const match = yield this.socket.send(cmd, /^\+OK "value":(.+)|-ERR/, tags);
                            if (match)
                                this.handlePublishToken(chan.instanceTag, match[1]);
                            numTelephonyPolled++;
                        }
                    }
                    if (numTelephonyPolled === 0) {
                        const serialNumber = yield this.serialNumber.get(['get.serialNumber', 'poll']);
                        this.state.serialNumber.update(serialNumber);
                    }
                }),
                intervalMs: 5000,
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.publishTokenSubscriptionId !== undefined) {
                this.socket.unregisterAsync(this.publishTokenSubscriptionId);
            }
            this.timeouts.clearAll();
            this.isCalling = {};
            const telephonyAttributes = {}; // instanceTag -> attribute
            for (const chan of this.configChannels) {
                if (chan.type === 'telephonyControlStatus') {
                    if (!(chan.instanceTag in telephonyAttributes)) {
                        telephonyAttributes[chan.instanceTag] = chan.attribute;
                    }
                    continue;
                }
                const index = chan.index === undefined ? '' : ` ${chan.index}`;
                this.socket.sendAsync(`"${chan.instanceTag}" unsubscribe ${chan.attribute}${index} "${chan.token}"`, ['configuring', `${chan.key}.unsubscribe`]);
            }
            for (const [instanceTag, attribute] of Object.entries(telephonyAttributes)) {
                this.socket.sendAsync(`"${instanceTag}" unsubscribe ${attribute} "${instanceTag}"`, [
                    'configuring',
                    `${instanceTag}.unsubscribe`,
                ]);
            }
            this.configChannels.length = 0;
        });
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.publishTokenSubscriptionId !== undefined) {
                this.socket.unregisterAsync(this.publishTokenSubscriptionId);
            }
            this.publishTokenSubscriptionId = this.socket.registerAsync(/!\s*"publishToken":"(.+)"\s*"value":(?:"(.+)"|(.+))/, match => { var _a; return this.handlePublishToken(match[1], (_a = match[2]) !== null && _a !== void 0 ? _a : match[3]); });
            const telephonyAttributes = {}; // instanceTag -> attribute
            for (const chan of this.configChannels) {
                if (chan.type === 'telephonyControlStatus') {
                    if (!(chan.instanceTag in telephonyAttributes)) {
                        telephonyAttributes[chan.instanceTag] = chan.attribute;
                    }
                    continue;
                }
                const index = chan.index === undefined ? '' : ` ${chan.index}`;
                const throttle = chan.type === 'telephonyReceiveGain' || chan.type === 'volume' ? ' 300' : '';
                this.socket.sendAsync(`"${chan.instanceTag}" subscribe ${chan.attribute}${index} "${chan.token}"${throttle}`, ['controlling', `${chan.key}.subscribe`]);
            }
            for (const [instanceTag, attribute] of Object.entries(telephonyAttributes)) {
                this.socket.sendAsync(`"${instanceTag}" subscribe ${attribute} "${instanceTag}"`, [
                    'controlling',
                    `${instanceTag}.subscribe`,
                ]);
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.publishTokenSubscriptionId !== undefined) {
                this.socket.unregisterAsync(this.publishTokenSubscriptionId);
            }
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            let result = 'verifyFailed';
            let initialResp;
            const tags = ['initializing'];
            try {
                initialResp = yield this.socket.waitFor(/Welcome to the Tesira Text Protocol Server|login:\s*/i, { receiveTimeoutMs: 10000, unterminatedRegExp: /login:\s*/i, name: 'login' });
            }
            catch (initialError) {
                yield this.socket.send('aveo', /ERR address not found:|Welcome to the Tesira Text Prototcol Server/i, tags);
                result = 'connected';
            }
            if (initialResp !== undefined) {
                if (initialResp[0].toLowerCase().includes('welcome')) {
                    result = 'connected';
                }
                else {
                    if (this.socket.options.socketType === 'plain') {
                        this.socket.sendRawAsync(`\xff\xfe\x01`, tags); // disable echo
                    }
                    const loginResp = yield this.socket.send((_a = credentials.username) !== null && _a !== void 0 ? _a : '', /.+Password:\s+|Welcome to the Tesira Text Protocol Server/i, tags, {
                        sendTimeoutMs: 4000,
                        unterminatedRegExp: /.+Password:\s+/i,
                    });
                    if (loginResp[0].toLowerCase().includes('welcome')) {
                        result = 'connected';
                    }
                    else {
                        const passwordResp = yield this.socket.send((_b = credentials.password) !== null && _b !== void 0 ? _b : '', /Welcome to the Tesira Text Protocol Server|Login incorrect/i, { sendTimeoutMs: 10000 });
                        if (passwordResp[0].toLowerCase().includes('welcome')) {
                            result = 'connected';
                        }
                        else if (passwordResp[0].toLowerCase().startsWith('login incorrect')) {
                            result = 'authFailed';
                        }
                    }
                }
            }
            if (result === 'connected') {
                yield this.socket.send(`SESSION set verbose true`, /^\+OK/, ['initializing']);
                if (!(yield this.hasPermission())) {
                    this.log.info(`insufficient permissions on this login`);
                    return 'authFailed';
                }
            }
            return result;
        });
    }
    // other class methods
    getBlock(instanceTag) {
        return this.blocks.find(block => block.instanceTag === instanceTag);
    }
    getConfigChannel(key) {
        return this.configChannels.find(chan => chan.key === key);
    }
    getConfigChannelByToken(token) {
        return this.configChannels.find(chan => chan.token === token);
    }
    getKey(config) {
        if ('mutePreset' in config) {
            return this.getKey(config.muteStatus);
        }
        else if ('instanceTag' in config) {
            const key = config.index === undefined
                ? config.instanceTag
                : `${config.instanceTag}.${config.index}`;
            if (!this.getBlock(config.instanceTag))
                return `invalid.${key}`;
            return key;
        }
        else if ('controlStatus' in config) {
            return this.getKey(config.controlStatus);
        }
        throw new Error(`invalid config: ${JSON.stringify(config)}`);
    }
    getTelephonyInstanceTags() {
        const instanceTags = [];
        for (const chan of this.configChannels) {
            if (chan.type === 'telephonyControlStatus' &&
                !instanceTags.includes(chan.instanceTag)) {
                instanceTags.push(chan.instanceTag);
            }
        }
        return instanceTags;
    }
    handlePublishToken(token, value) {
        const chan = this.getConfigChannelByToken(token);
        if (chan !== undefined) {
            if (chan.type === 'mute') {
                this.state.mutes.update(chan.key, value === 'true');
            }
            else if (chan.type === 'volume') {
                this.state.volumes.update(chan.key, parseFloat(value));
            }
            else if (chan.type === 'telephonyReceiveGain') {
                this.state.calls.updateReceiveGain(chan.controlStatusKey, parseFloat(value));
            }
            else if (chan.type === 'presetMute') {
                this.state.mutes.update(chan.key, value === 'true');
            }
            else {
                this.log.warning(`Unknown chan type: ${JSON.stringify(chan)}`);
            }
            return;
        }
        const telephonyInstanceTags = this.getTelephonyInstanceTags();
        if (telephonyInstanceTags.includes(token)) {
            const callStatuses = this.parseTesiraCallStateInfo(token, value);
            for (const [key, callStatus] of Object.entries(callStatuses)) {
                const chan = this.getConfigChannel(key);
                if (chan === undefined)
                    continue;
                let extras;
                if (callStatus.contactName !== undefined || callStatus.dialString !== undefined) {
                    extras = {};
                    if (callStatus.contactName !== undefined) {
                        extras.contactName = callStatus.contactName;
                    }
                    if (callStatus.dialString !== undefined) {
                        extras.dialString = callStatus.dialString;
                    }
                }
                let handled = false;
                if (callStatus.callState === 'ringing') {
                    if (callStatus.direction === 'incoming') {
                        this.state.calls.setIncoming(key, extras);
                    }
                    else {
                        const callType = chan.block.isVoip ? 'voip' : 'pstn';
                        this.state.calls.setOutgoing(key, callType, extras);
                    }
                    handled = true;
                }
                else if (callStatus.callState === 'connected') {
                    this.state.calls.connect(key, extras);
                    handled = true;
                }
                else if (callStatus.callState === 'disconnecting') {
                    // none of the Tesira call states translate to disconnecting
                }
                else if (callStatus.callState === 'disconnected') {
                    // Check isCalling before disconnecting so we don't disconnect the call if a
                    // poll response is received just as we're placing a call.
                    if (!this.isCalling[key])
                        this.state.calls.disconnect(key, extras);
                    handled = true;
                }
                if (!handled)
                    this.state.calls.updateCall(key, extras);
            }
        }
    }
    hasPermission() {
        return __awaiter(this, void 0, void 0, function* () {
            // The Tesira can have different login accounts with different access levels. We do a
            // simplified version of the channel detection to try and figure out whether or not we have
            // sufficient permission to control the system.
            let match = yield this.socket.send(`SESSION get aliases`, /^\+OK "list":\["(.+)"\]$/);
            const aliases = match[1].split('" "');
            for (const alias of aliases) {
                match = yield this.socket.send(`"${alias}" get aveo`, /^-ERR 'aveo' is not supported by (.+)::(.+)$|^-ERR address not found:/);
                const blockType = match[1] === undefined ? 'unknown' : match[1];
                const attr = blockType === 'LogicMeterInterface' ? 'states' : 'numChannels';
                match = yield this.socket.send(`"${alias}" get ${attr}`, /\+OK "value":(.+)|(-ERR .+)/);
                if (match[1] !== undefined)
                    return true;
                if (match[2] !== undefined && match[2] === '-ERR PERMISSION_DENIED')
                    return false;
            }
            return true; // if there are no blocks to check, assume we have permission
        });
    }
    parseTesiraCallerId(cid) {
        let contactName;
        let dialString;
        const cidMatch = /^"(.+)"$/.exec(cid);
        if (cidMatch) {
            const fields = cidMatch[1].split('""');
            if (fields.length === 3) {
                const numberField = fields[1];
                const nameField = fields[2];
                if (nameField.length > 0) {
                    contactName = nameField;
                    if (numberField.length > 0) {
                        dialString = numberField;
                    }
                }
                else if (numberField.length > 0) {
                    contactName = numberField;
                    dialString = numberField;
                }
            }
        }
        return { contactName, dialString };
    }
    parseTesiraCallStateInfo(instanceTag, value) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        let callState;
        try {
            callState = JSON.parse(value
                .replace(/:([^ [\]{}"]+)/g, ':"$1"')
                .replace(/"\s+"/g, '", "')
                .replace(/}\s*{/g, '}, {'));
        }
        catch (_j) {
            this.log.warning(`failed parsing call state info`);
            return {};
        }
        if (callState.callStateInfo === undefined) {
            this.log.error(`missing callStateInfo in callState response`);
            return {};
        }
        if (!Array.isArray(callState.callStateInfo)) {
            this.log.error(`unexpected callStateInfo format`);
            return {};
        }
        const callStatuses = {};
        for (const info of callState.callStateInfo) {
            const callId = parseInt((_b = (_a = info === null || info === void 0 ? void 0 : info.callId) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '');
            if (Number.isNaN(callId)) {
                this.log.error(`invalid callId in callState response`);
                continue;
            }
            const lineId = parseInt((_d = (_c = info === null || info === void 0 ? void 0 : info.lineId) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : '');
            if (Number.isNaN(lineId)) {
                this.log.error(`invalid lineId in callState response`);
                continue;
            }
            if (callId !== 0)
                continue; // we only support the first call appearance
            const { callState, direction } = this.translateTesiraCallState((_f = (_e = info === null || info === void 0 ? void 0 : info.state) === null || _e === void 0 ? void 0 : _e.toString()) !== null && _f !== void 0 ? _f : '');
            const { contactName, dialString } = this.parseTesiraCallerId((_h = (_g = info === null || info === void 0 ? void 0 : info.cid) === null || _g === void 0 ? void 0 : _g.toString()) !== null && _h !== void 0 ? _h : '');
            const callStatus = {};
            if (callState !== undefined)
                callStatus.callState = callState;
            if (contactName !== undefined)
                callStatus.contactName = contactName;
            if (dialString !== undefined)
                callStatus.dialString = dialString;
            if (direction !== undefined)
                callStatus.direction = direction;
            callStatuses[`${instanceTag}.${lineId + 1}`] = callStatus;
        }
        return callStatuses;
    }
    translateTesiraCallState(tesiraCallState) {
        switch (tesiraCallState) {
            case 'VOIP_CALL_STATE_INIT':
            case 'VOIP_CALL_STATE_FAULT':
            case 'VOIP_CALL_STATE_IDLE':
            case 'TI_CALL_STATE_INIT':
            case 'TI_CALL_STATE_FAULT':
            case 'TI_CALL_STATE_IDLE':
            case 'TI_CALL_STATE_DROPPED':
                return { callState: 'disconnected', direction: undefined };
            case 'VOIP_CALL_STATE_DIALTONE':
            case 'VOIP_CALL_STATE_SILENT':
            case 'VOIP_CALL_STATE_DIALING':
            case 'VOIP_CALL_STATE_RINGBACK':
            case 'VOIP_CALL_STATE_BUSY':
            case 'VOIP_CALL_STATE_INVALID_NUMBER':
            case 'TI_CALL_STATE_DIALING':
            case 'TI_CALL_STATE_RINGBACK':
            case 'TI_CALL_STATE_BUSY_TONE':
            case 'TI_CALL_STATE_ERROR_TONE':
                return { callState: 'ringing', direction: 'outgoing' };
            case 'VOIP_CALL_STATE_RINGING':
            case 'VOIP_CALL_STATE_REJECT':
            case 'TI_CALL_STATE_RINGING':
                return { callState: 'ringing', direction: 'incoming' };
            case 'VOIP_CALL_STATE_ACTIVE':
            case 'VOIP_CALL_STATE_ACTIVE_MUTED':
            case 'VOIP_CALL_STATE_ON_HOLD':
            case 'VOIP_CALL_STATE_WAITING_RING':
            case 'VOIP_CALL_STATE_CONF_ACTIVE':
            case 'VOIP_CALL_STATE_CONF_HOLD':
            case 'TI_CALL_STATE_CONNECTED':
            case 'TI_CALL_STATE_CONNECTED_MUTED':
                return { callState: 'connected', direction: undefined };
            case 'VOIP_CALL_STATE_ANSWER_CALL':
            default:
                break;
        }
        this.log.error(`unexpected callState: ${tesiraCallState}`);
        return { callState: undefined, direction: undefined };
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            if ('mutePreset' in config) {
                // We can't validate preset names on the Tesira, so just validate the mute status.
                const statusFilters = {
                    assistiveListeningMute: 'assistiveListeningMuteStatus',
                    audioInputMute: 'audioInputMuteStatus',
                    mics: 'micsMuteStatus',
                    overflowRoomMute: 'overflowRoomMuteStatus',
                    soundReinforcementMute: 'soundReinforcementMuteStatus',
                };
                const statusFilter = statusFilters[info.filter];
                if (statusFilter === undefined) {
                    this.log.error(`no mute status filter for: ${info.filter}`);
                    return 'incompatible';
                }
                const key = this.getKey(config);
                const block = this.getBlock(config.muteStatus.instanceTag);
                if (block === undefined)
                    return 'missing';
                const result = block.validate(config.muteStatus, statusFilter);
                if (result === 'ok') {
                    this.configChannels.push({
                        attribute: block.muteAttribute,
                        block,
                        index: config.muteStatus.index,
                        instanceTag: config.muteStatus.instanceTag,
                        key,
                        mute: config.mutePreset,
                        token: `${key}.${block.muteAttribute}`,
                        type: 'presetMute',
                        unmute: config.unmutePreset,
                    });
                }
                return result;
            }
            else if ('instanceTag' in config) {
                const key = this.getKey(config);
                const block = this.getBlock(config.instanceTag);
                if (block === undefined)
                    return 'missing';
                const result = block.validate(config, info.filter);
                if (result === 'ok') {
                    const attribute = info.type === 'mute' ? block.muteAttribute : block.volumeAttribute;
                    this.configChannels.push({
                        attribute,
                        block,
                        index: config.index,
                        instanceTag: config.instanceTag,
                        key,
                        token: `${key}.${attribute}`,
                        type: info.type === 'mute' ? 'mute' : 'volume',
                    });
                }
                return result;
            }
            this.log.error(`invalid config: ${JSON.stringify(config)}`);
            return 'missing';
        });
    }
    validateTelephony(config) {
        return __awaiter(this, void 0, void 0, function* () {
            if ('controlStatus' in config) {
                const controlStatusKey = this.getKey(config);
                const controlStatusBlock = this.getBlock(config.controlStatus.instanceTag);
                const controlStatusResult = controlStatusBlock === undefined
                    ? 'missing'
                    : controlStatusBlock.validate(config.controlStatus, 'telephony');
                const validated = { controlStatus: controlStatusResult };
                if (controlStatusBlock && controlStatusResult === 'ok') {
                    this.configChannels.push({
                        attribute: 'callState',
                        block: controlStatusBlock,
                        index: config.controlStatus.index,
                        instanceTag: config.controlStatus.instanceTag,
                        key: controlStatusKey,
                        token: `${controlStatusKey}.callState`,
                        type: 'telephonyControlStatus',
                    });
                }
                if (config.receiveGain !== undefined) {
                    const receiveGainKey = this.getKey(config.receiveGain);
                    const receiveGainBlock = this.getBlock(config.receiveGain.instanceTag);
                    const receiveGainResult = receiveGainBlock === undefined
                        ? 'missing'
                        : receiveGainBlock.validate(config.receiveGain, 'receiveGain');
                    validated.receiveGain = receiveGainResult;
                    if (controlStatusResult === 'ok' &&
                        receiveGainBlock &&
                        receiveGainResult === 'ok') {
                        this.configChannels.push({
                            attribute: receiveGainBlock.volumeAttribute,
                            block: receiveGainBlock,
                            controlStatusKey,
                            index: config.receiveGain.index,
                            instanceTag: config.receiveGain.instanceTag,
                            key: receiveGainKey,
                            token: `${receiveGainKey}.${receiveGainBlock.volumeAttribute}`,
                            type: 'telephonyReceiveGain',
                        });
                    }
                }
                return validated;
            }
            this.log.error(`invalid config: ${JSON.stringify(config)}`);
            return { controlStatus: 'missing' };
        });
    }
}
//static properties
Tesira.channelsConfigName = 'tesiraControlChannels';
Tesira.driverType = 'socketDriver';
Tesira.key = '-KVMNDN3teZc3mpr3I3z';
Tesira.baseSocketOptions = {
    defaultCredentials: {
        username: 'default',
        password: 'default',
    },
    sendTerminator: '\n',
    sendTimeoutMs: 3000,
};
Tesira.socketOptions = [
    Object.assign(Object.assign({}, Tesira.baseSocketOptions), { port: 22, socketType: 'ssh' }),
    Object.assign(Object.assign({}, Tesira.baseSocketOptions), { port: 23, negotiateTelnet: true }),
    Object.assign(Object.assign({}, Tesira.baseSocketOptions), { port: 4999 }),
];
